// The league channel rendered on the FeedScreen template (CHAT.md): user,
// AI, and system messages, reactions on tap, a filter for talk versus the
// record, and the composer. Used by the shell's chat drawer and by the draft
// room's chat pane.
//
// ONE PANEL FOR A PRACTICE LEAGUE AND A REAL ONE (PLAN-2026-09-26-chat C3): it
// draws the shell's feed (`useChatDrawer().feed`, `data/chat.ts`), which reads
// the simulator or the API by which league the URL names. A direct
// conversation (I8) is drawn by the same `Channel`, on its own feed, with no
// filter, no starters and no Hide. Clean (LOOKS.md Part 7): drawn through the
// kit, so a look restyles it.
//
// EMOJI (PLAN-2026-10-27-more-fun-in-chat C1): the box types them, a message's
// bar reacts with any of the site's set past the quick five, a message of one
// to three emoji alone is drawn large, and the open bar says who reacted.
//
// REPLIES AND MENTIONS (C2): the bar's Reply puts the line over the box, and
// the message sent carries it, quoted over the reply, a tap on the quote going
// back to the line. `@` in the league's channel offers its other managers; a
// name a message mentions is marked, and a message that mentions me is too.
//
// STICKERS (PLAN-2026-10-30-stickers): the box's sticker button opens every
// sticker drawn with my team's character, and one sent is drawn large in the
// feed with the team it was sent with, played once and again on a tap.
//
// POLLS (PLAN-2026-10-30-polls-in-chat): *New poll* beside the filter opens
// the poll's form where the pickers open, in a real league's channel; a poll
// in the feed is its card, a tap on an option a vote.

import { useAccountPrefs } from '../data/accountPrefs';
import { useMemo, useState } from 'react';
import { avatarColor, useSession } from '../data/session';
import {
  CHAT_BODY_MAX,
  CHAT_REACTION_KINDS_MAX,
  CHAT_REACTIONS,
  chatEmoji,
  emojiOnly,
  splitMentions,
  valueOf,
  type ChatMessage,
  type ChatReaction,
  type MatchupView,
  type StandingsView,
  type StickerId,
} from '@fsp/domain';
import { useMatchup, useMyMatchupId, useStandings } from '../data/hooks';
import { useMyTeamHere } from '../data/team';
import { useActiveLeague } from '../data/LeaguesProvider';
import { EmojiPicker } from '../kit/EmojiPicker';
import {
  AvatarMark,
  Button,
  Callout,
  IconButton,
  Ink,
  Segments,
  tell,
  type WornFace,
} from '../kit/ui';
import { StickerArt, type StickerTeam } from '../kit/StickerArt';
import { isPhoneWidth, useViewportWidth } from '../kit/viewport';
import { FeedScreen } from '../templates/FeedScreen';
import { ChatCardBody } from './ChatCards';
import { useChatDrawer } from './chatDrawer';
import { useEditTeam } from './editTeam';
import { PollCard } from './PollCard';
import { PollComposer } from './PollComposer';
import { ShareTray } from './ShareTray';
import { StickerTray } from './StickerTray';
import type { ChatFeed } from '../data/chat';
import { tourDone } from '../demo/chapters';

/**
 * A writer with no team's color — a former manager, a spectator, a
 * commissioner who runs no team: a quiet grey disc for their initial.
 */
const NO_AUTHOR_COLOR = '#6b7280';

/**
 * Trash-talk starters (CHAT.md, scenario I2): three lines about my matchup
 * as it stands, true by construction, for the sender to edit and own. The
 * app never posts them on its own.
 */
export function trashTalkStarters(matchup: MatchupView | null, standings: StandingsView): string[] {
  if (!matchup || matchup.status === 'pre') return [];
  const meHome = matchup.home.team.isMine;
  const me = meHome ? matchup.home : matchup.away;
  const them = meHome ? matchup.away : matchup.home;
  const who = them.team.ownerName;
  const margin = Math.abs(me.score - them.score).toFixed(1);
  const out: string[] = [];
  // A final goes by who won it, which on a broken tie is not the higher score (N1).
  const final = matchup.status === 'final';
  const ahead = final ? matchup.winnerTeamId === me.team.id : me.score > them.score;
  const behind = final ? matchup.winnerTeamId === them.team.id : me.score < them.score;
  if (ahead) {
    out.push(
      matchup.status === 'final'
        ? `That's ${me.score.toFixed(1)} to ${them.score.toFixed(1)}, ${who}. Go tell someone.`
        : `Up ${margin} on you, ${who}, and I've barely started.`,
    );
  } else if (behind) {
    out.push(
      matchup.status === 'final'
        ? `Enjoy it, ${who}. Next week is a different story.`
        : `Enjoy the lead, ${who}. I've got ${me.yetToPlay} still to play.`,
    );
  } else out.push(`Tied, ${who}. Somebody blink.`);
  const theirEmpty = them.rows.filter((r) => !r.player).length;
  if (theirEmpty)
    out.push(
      `Somebody left ${theirEmpty === 1 ? 'a slot' : `${theirEmpty} slots`} empty, and it wasn't me, ${who}.`,
    );
  else if ((them.benchPoints ?? 0) > them.score && them.score > 0)
    out.push(`${who}, your bench outscored your starters. Bold strategy.`);
  const rivalry = matchup.rivalry;
  if (rivalry?.streakTeamId === me.team.id && rivalry.streak >= 2)
    out.push(`That makes ${rivalry.streak} straight, ${who}. I'm not counting. I am counting.`);
  const myRank = standings.rows.find((r) => r.team.id === me.team.id)?.rank;
  const theirRank = standings.rows.find((r) => r.team.id === them.team.id)?.rank;
  if (myRank && theirRank && myRank < theirRank)
    out.push(
      `Standings say ${ordinal(myRank)} plays ${ordinal(theirRank)} this week. Just saying.`,
    );
  return out.slice(0, 3);
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/**
 * A writer with no team in the league, drawn as a team of one
 * (PLAN-2026-10-30-stickers §3): their own face, or their initial, on their
 * color.
 */
function teamOfOne(name: string, color: string, face: WornFace | null): StickerTeam {
  return {
    abbreviation: (name.trim()[0] ?? '?').toUpperCase(),
    colors: face?.colors ?? { primary: color, secondary: color },
    badge: face === null ? null : { kind: 'face', face: face.face },
  };
}

type Filter = 'all' | 'talk' | 'record';

const FILTERS: readonly { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'talk', label: 'Talk' },
  { id: 'record', label: 'Record' },
];

export function ChatPanel({ compact = false }: { compact?: boolean }) {
  // A REAL LEAGUE IN SETUP HAS NO MATCHUP OR STANDINGS TO ASK FOR — both are
  // refused until it has a calendar — and so no starters; asking anyway is a
  // pair of refusals every time the drawer opens.
  const setup = useActiveLeague()?.league.status === 'setup';
  return setup ? (
    <Channel compact={compact} starters={NO_STARTERS} />
  ) : (
    <WithStarters compact={compact} />
  );
}

const NO_STARTERS: string[] = [];

function WithStarters({ compact }: { compact: boolean }) {
  const matchup = valueOf(useMatchup(useMyMatchupId()));
  // `valueOf`, not the snapshot: the composer's trash-talk starters are a
  // nicety, so a league whose standings have not arrived gets no prompts
  // rather than a chat panel that refuses to draw (E12).
  const standings = valueOf(useStandings());
  const starters = useMemo(
    () => (standings === null ? [] : trashTalkStarters(matchup, standings)),
    [matchup, standings],
  );
  return <Channel compact={compact} starters={starters} />;
}

export function Channel({
  compact,
  starters,
  feed: given,
  conversationWith,
}: {
  compact: boolean;
  starters: string[];
  /** A conversation's own feed; the league's, from the drawer, otherwise. */
  feed?: ChatFeed;
  /** Who a conversation is with, by name: the panel is a conversation's. */
  conversationWith?: string;
}) {
  const drawer = useChatDrawer();
  const feed = given ?? drawer.feed;
  const direct = conversationWith !== undefined;
  // A MUTED READER SEES NO BOT (B-299): a real league's chat leaves them out
  // already; the simulator's league is this client's own, so it does here.
  const hideBots = useAccountPrefs().account?.chat?.hideBots ?? false;
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<string | null>(null);
  /** The message whose reaction picker is open over the box, past the quick five. */
  const [reacting, setReacting] = useState<string | null>(null);
  /** The line the next message answers (C2). */
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  /** The line a quote just went back to, marked a moment so the eye finds it. */
  const [flash, setFlash] = useState<string | null>(null);
  /** How many times each sticker was tapped: a tap plays it again. */
  const [replays, setReplays] = useState<Record<string, number>>({});
  /** The poll's form is open over the box (PLAN-2026-10-30-polls-in-chat). */
  const [asking, setAsking] = useState(false);
  const vote = async (m: ChatMessage, option: number | null) => {
    const done = await feed.vote?.(m.id, option);
    if (done && !done.ok) tell(done.message, 'bad');
  };
  // MY CHARACTER FOR THE TRAY: my team in this league, else me as a team of
  // one; the foot offers a face to a team whose badge is not one.
  const here = useMyTeamHere();
  const myTeam = here?.team ?? null;
  const { session } = useSession();
  const editTeam = useEditTeam();
  const mine: StickerTeam =
    myTeam !== null
      ? { abbreviation: myTeam.abbreviation, colors: myTeam.colors, badge: myTeam.badge }
      : teamOfOne(
          session?.displayName ?? 'You',
          session ? avatarColor(session.avatarSeed) : NO_AUTHOR_COLOR,
          session?.avatarFace ?? null,
        );
  // THE FOOT ONLY WHERE A FACE MADE WOULD SHOW: the practice league keeps no
  // badge for my team, so its stickers stay a placard whatever Edit team does.
  const facing =
    myTeam === null ||
    here?.source !== 'api' ||
    myTeam.badge?.kind === 'face' ||
    myTeam.badge?.kind === 'bot';
  const stickerHeight = useViewportWidth((w) => (isPhoneWidth(w) ? 120 : 140));
  // THE COMMISSIONER'S HIDE (I9, B-449), in a real league, on what a person or
  // a bot wrote: never a system line or a card.
  const commissioner = useActiveLeague()?.league.isCommissioner === true && !direct;
  // A shared player or game carries its writer's words, and hides like them;
  // a card the site drew does not (PLAN-2026-10-31-players-and-games-in-chat).
  const canHide = (m: ChatMessage) =>
    commissioner &&
    feed.hide !== undefined &&
    m.kind !== 'system' &&
    (m.card === undefined || m.card.kind === 'player' || m.card.kind === 'matchup');
  const hide = async (m: ChatMessage) => {
    const done = await feed.hide?.(m.id, !(m.hidden ?? false));
    if (done && !done.ok) tell(done.message, 'bad');
    else if (done) tell(m.hidden ? 'Shown again.' : 'Hidden for everyone.', 'good');
  };

  const items = useMemo(
    () =>
      feed.messages.filter(
        (m) =>
          !(hideBots && m.kind === 'ai') &&
          (filter === 'all' ? true : filter === 'talk' ? m.kind !== 'system' : m.kind === 'system'),
      ),
    [feed.messages, filter, hideBots],
  );

  const react = async (m: ChatMessage, emoji: ChatReaction, on: boolean) => {
    const done = await feed.react(m.id, emoji, on);
    if (!done.ok) tell(done.message, 'bad');
    else tourDone('react');
  };

  // THE WRITER'S FACE BESIDE THE NAME (B-525): theirs, a bot's character, or
  // their initial on their team's color.
  const who = (m: ChatMessage) => (
    <span className="fsp-chat-who">
      <AvatarMark
        name={m.author}
        color={m.authorColor ?? NO_AUTHOR_COLOR}
        face={m.authorFace as WornFace | null}
        size="sm"
      />
      <Ink color={m.authorColor}>{m.author}</Ink>
    </span>
  );
  /** What a message is called to a screen reader: whose it is. */
  const whose = (m: ChatMessage) => (m.kind === 'system' ? 'this line' : `${m.author}’s message`);
  const toggle = (m: ChatMessage, line?: HTMLElement | null) => {
    setOpen((o) => (o === m.id ? null : m.id));
    setReacting(null);
    // THE BAR IN SIGHT: opened on the last line, it would grow below the fold.
    if (line && open !== m.id)
      requestAnimationFrame(() => line.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  };
  /** The words, with each `@Name` it mentions marked, mine more so. */
  const words = (m: ChatMessage) => {
    const named = m.mentions ?? [];
    if (named.length === 0) return m.body;
    const me = new Set(named.filter((x) => x.me).map((x) => x.name));
    return splitMentions(
      m.body,
      named.map((x) => x.name),
    ).map((p, i) =>
      p.name === undefined ? (
        p.text
      ) : (
        <strong key={i} className="fsp-chat-mention" data-me={me.has(p.name) || undefined}>
          {p.text}
        </strong>
      ),
    );
  };
  /**
   * Back to the line a quote answers, in this feed: shown first when the
   * filter leaves it out, and said so when it is a muted bot's or further back
   * than the page.
   */
  const jump = (id: string, from: HTMLElement) => {
    const list = from.closest('.fsp-feed-list');
    const held = feed.messages.find((m) => m.id === id);
    if (held && hideBots && held.kind === 'ai') {
      tell('That is a bot’s line, and you have muted the bots.');
      return;
    }
    if (held && !items.includes(held)) setFilter('all');
    const go = () => {
      const line = list?.querySelector<HTMLElement>(`[data-msg="${CSS.escape(id)}"]`);
      if (!line) {
        tell('That line is further back than the chat shows.');
        return;
      }
      line.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setFlash(id);
      window.setTimeout(() => setFlash((f) => (f === id ? null : f)), 1600);
    };
    // A FILTER JUST CHANGED draws the line on the next frame.
    if (held && !items.includes(held)) requestAnimationFrame(() => requestAnimationFrame(go));
    else go();
  };
  /** The line a message answers, small over it. */
  const quote = (m: ChatMessage) => {
    const q = m.replyTo;
    if (!q) return null;
    return (
      <button
        type="button"
        className="fsp-chat-quote"
        data-gone={q.gone || undefined}
        disabled={q.gone === true}
        aria-label={
          q.gone
            ? 'Answering a message that is gone'
            : `Answering ${q.author ?? 'the league’s line'}: ${q.body}. Go to it.`
        }
        onClick={(e) => {
          e.stopPropagation();
          jump(q.id, e.currentTarget);
        }}
      >
        {q.author ? <span className="fsp-chat-quote-who">{q.author}</span> : null}
        <span className="fsp-chat-quote-body">{q.body}</span>
      </button>
    );
  };
  const render = (m: ChatMessage) => {
    const reactions = Object.entries(m.reactions);
    const mine = new Set(m.myReactions ?? []);
    const showBar = open === m.id;
    // ONE TO THREE EMOJI ALONE, drawn large; never a hidden line or a card.
    const big = m.hidden || m.card !== undefined ? 0 : emojiOnly(m.body);
    const body = m.hidden ? (
      <span className="fsp-chat-hidden">{m.body}</span>
    ) : big > 0 && big <= 3 ? (
      <span className="fsp-chat-big">{m.body}</span>
    ) : (
      <span>{words(m)}</span>
    );
    const full = reactions.length >= CHAT_REACTION_KINDS_MAX;
    return (
      <div
        className="fsp-chat-msg"
        data-kind={m.kind}
        data-msg={m.id}
        data-open={showBar || undefined}
        data-flash={flash === m.id || undefined}
        data-me={m.mentions?.some((x) => x.me) || undefined}
        onClick={(e) => {
          // A TAP ON A STICKER PLAYS IT AGAIN, and opens the bar as on any line.
          if (m.card?.kind === 'sticker' && (e.target as Element).closest('.fsp-chat-sticker'))
            setReplays((r) => ({ ...r, [m.id]: (r[m.id] ?? 0) + 1 }));
          toggle(m, e.currentTarget);
        }}
      >
        {/* THE BAR FROM THE KEYBOARD (UX-29): a tap on the line opens it; this
            is there for a key, and shows only once it has the focus. */}
        <button
          type="button"
          className="fsp-chat-act"
          aria-expanded={showBar}
          onClick={(e) => {
            e.stopPropagation();
            toggle(m, e.currentTarget.parentElement);
          }}
        >
          {`React to ${whose(m)}`}
        </button>
        {quote(m)}
        {m.kind === 'system' ? (
          m.card ? (
            <ChatCardBody card={m.card} />
          ) : (
            <span>{m.body}</span>
          )
        ) : m.card?.kind === 'sticker' ? (
          // A STICKER, large and with no bubble, under its writer's name; the
          // team it was sent with, or the writer as a team of one.
          <>
            {who(m)}
            {m.kind === 'ai' ? <span className="fsp-chat-ai">AI</span> : null}
            <span className="fsp-chat-sticker">
              <StickerArt
                key={replays[m.id] ?? 0}
                sticker={m.card.sticker}
                words={m.card.words}
                team={
                  m.card.team ??
                  teamOfOne(
                    m.author,
                    m.authorColor ?? NO_AUTHOR_COLOR,
                    m.authorFace as WornFace | null,
                  )
                }
                height={stickerHeight}
              />
              <span className="fsp-sr">{`${m.author} sent a sticker: ${m.card.words}`}</span>
            </span>
          </>
        ) : m.card ? (
          // A MANAGER'S OWN CARD — a mock they shared (MOCK_DRAFT.md §6.1), a
          // player or a game: their name over it, and a share's words, when
          // they wrote some, between the two.
          <>
            {who(m)}
            {(m.card.kind === 'player' || m.card.kind === 'matchup') && m.card.words ? (
              <> {body}</>
            ) : null}
            <ChatCardBody card={m.card} />
          </>
        ) : m.poll ? (
          // A POLL, its question and options in place of its words.
          <>
            {who(m)}
            <PollCard
              poll={m.poll}
              open={showBar}
              {...(feed.vote ? { onVote: (option: number | null) => void vote(m, option) } : {})}
            />
          </>
        ) : (
          <>
            {who(m)}
            {m.kind === 'ai' ? <span className="fsp-chat-ai">AI</span> : null} {body}
          </>
        )}
        {reactions.length || showBar ? (
          <div className="fsp-chat-reactions">
            {reactions.map(([emoji, who]) => (
              <button
                key={emoji}
                type="button"
                className="fsp-chat-reaction"
                data-mine={mine.has(emoji) || undefined}
                aria-pressed={mine.has(emoji)}
                aria-label={`${emoji} ${who.length}: ${who.join(', ')}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (chatEmoji(emoji) !== undefined) void react(m, emoji, !mine.has(emoji));
                }}
              >
                {emoji} {who.length}
              </button>
            ))}
            {showBar
              ? CHAT_REACTIONS.filter((r) => !m.reactions[r]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className="fsp-chat-reaction fsp-chat-reaction-add"
                    aria-label={`React ${r}`}
                    disabled={full}
                    onClick={(e) => {
                      e.stopPropagation();
                      void react(m, r, true);
                      setOpen(null);
                    }}
                  >
                    {r}
                  </button>
                ))
              : null}
            {showBar && !full ? (
              <IconButton
                icon="react"
                label="More reactions"
                variant="quiet"
                size="sm"
                aria-expanded={reacting === m.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setReacting((r) => (r === m.id ? null : m.id));
                }}
              />
            ) : null}
            {showBar && !m.hidden ? (
              <button
                type="button"
                className="fsp-chat-reaction fsp-chat-reply"
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyingTo(m.id);
                  setReacting(null);
                  setOpen(null);
                }}
              >
                Reply
              </button>
            ) : null}
            {showBar && canHide(m) ? (
              <button
                type="button"
                className="fsp-chat-reaction fsp-chat-hide"
                onClick={(e) => {
                  e.stopPropagation();
                  void hide(m);
                  setOpen(null);
                }}
              >
                {m.hidden ? 'Show it again' : 'Hide'}
              </button>
            ) : null}
          </div>
        ) : null}
        {/* WHO REACTED, on the screen and not in a tooltip (UX-8). */}
        {showBar && reactions.length > 0 ? (
          <p className="fsp-chat-reacted">
            {reactions.map(([emoji, who]) => `${emoji} ${who.join(', ')}`).join(' · ')}
          </p>
        ) : null}
      </div>
    );
  };

  const reactingTo = reacting === null ? undefined : feed.messages.find((m) => m.id === reacting);
  const answering =
    replyingTo === null ? undefined : feed.messages.find((m) => m.id === replyingTo);

  return (
    <FeedScreen
      header={
        compact || direct ? undefined : (
          <div className="fsp-chat-head">
            <Segments label="Show" items={FILTERS} value={filter} onChange={setFilter} />
            {feed.ask ? (
              <Button
                size="xs"
                variant="soft"
                icon="poll"
                expanded={asking}
                onClick={() => {
                  setReacting(null);
                  setAsking((a) => !a);
                }}
              >
                New poll
              </Button>
            ) : null}
          </div>
        )
      }
      notice={
        feed.state === 'failed' ? (
          <Callout tone="warn">
            {feed.messages.length
              ? `${feed.problem ?? 'Chat could not be reached.'} Showing what was here; it will try again.`
              : `${feed.problem ?? 'Chat could not be reached.'} It will try again.`}
          </Callout>
        ) : null
      }
      items={items}
      renderItem={render}
      tray={
        reactingTo ? (
          <EmojiPicker
            key={`react|${reactingTo.id}`}
            title={`React to ${whose(reactingTo)}`}
            exclude={new Set(Object.keys(reactingTo.reactions))}
            onPick={(emoji) => {
              void react(reactingTo, emoji, true);
              setReacting(null);
              setOpen(null);
            }}
            onClose={() => setReacting(null)}
          />
        ) : asking && feed.ask && !direct ? (
          <PollComposer
            onAsk={async (poll) => {
              const done = await feed.ask!(poll);
              return done.ok ? null : done.message;
            }}
            onClose={() => setAsking(false)}
          />
        ) : undefined
      }
      onTrayAway={() => {
        setReacting(null);
        setAsking(false);
      }}
      composer={{
        placeholder: direct ? `Write to ${conversationWith}.` : 'Say something.',
        maxLength: CHAT_BODY_MAX,
        disabled: feed.state === 'loading',
        onSend: async (body) => {
          // WITH A PLAYER OR A GAME ATTACHED, the line carries it, with these
          // words or none; it stays attached when a send fails, as the words do.
          const sharing = feed.share !== undefined ? drawer.attachment : null;
          const sent =
            sharing !== null && feed.share !== undefined
              ? await feed.share(sharing.share, body === '' ? null : body, answering?.id ?? null)
              : await feed.post(body, answering?.id ?? null);
          if (!sent.ok) tell(sent.message, 'bad');
          else {
            setReplyingTo(null);
            if (sharing !== null) drawer.attach(null);
          }
          return sent.ok;
        },
        starters,
        emoji: true,
        // THE LEAGUE'S OTHER MANAGERS, for `@`; a conversation has one other
        // person, already told of every line.
        mentions: direct ? undefined : feed.people?.map((p) => p.name),
        replying: answering
          ? {
              id: answering.id,
              author: answering.kind === 'system' ? 'the league’s line' : answering.author,
              body: answering.body,
              onCancel: () => setReplyingTo(null),
            }
          : null,
        // A REAL LEAGUE'S, its room and its conversations
        // (PLAN-2026-10-31-players-and-games-in-chat §5).
        // TODO(I1): sharing in the practice league, whose simulated chat has no card a manager sends.
        ...(feed.share !== undefined && drawer.canShare
          ? {
              share: {
                draw: (close: () => void) => (
                  <ShareTray
                    onPick={(attachment) => {
                      // FROM THE BOX, it stays where the box is: the draft
                      // room's inline chat or a conversation.
                      drawer.attach(attachment, { open: false });
                      close();
                    }}
                    onClose={close}
                  />
                ),
                attached:
                  drawer.attachment === null
                    ? null
                    : {
                        label: drawer.attachment.label,
                        onRemove: () => drawer.attach(null),
                      },
              },
            }
          : {}),
        stickers: {
          onSend: async (sticker) => {
            const sent = await feed.sticker(sticker as StickerId, answering?.id ?? null);
            if (!sent.ok) tell(sent.message, 'bad');
            else setReplyingTo(null);
            return sent.ok;
          },
          draw: (send, close) => (
            <StickerTray
              team={mine}
              onPick={send}
              onClose={close}
              {...(facing
                ? {}
                : {
                    makeFace: () => {
                      drawer.setOpen(false);
                      editTeam.open();
                    },
                  })}
            />
          ),
        },
      }}
      empty={
        feed.state === 'loading'
          ? 'Reading the chat.'
          : direct
            ? `Nothing here yet. Only you and ${conversationWith} read this.`
            : 'Nobody has said anything yet. Be the first, ideally something regrettable.'
      }
    />
  );
}
